import sortBy from "lodash/sortBy";
import TransferInstruction, {
  NewTransferStatusType,
  OpenLoanUserDecision,
  TransferInitiationMethod,
  TransferInstructionResponse,
} from "../classes/transferInstruction";
import { AddressResponse } from "../classes/address";
import { TransferStatusTypes } from "../classes/transfer";
import TransferDestination, { DestinationType, TransferDestinationResponse } from "../classes/transferDestination";
import CoreAPI from "./coreAPI";

export type ProviderMailingDetails = {
  destinationProviderAddress: AddressResponse;
  rothDestinationAddress: AddressResponse;
};

export default function APITransferInstruction(api: CoreAPI) {
  const methods = {
    async getTransferWithInstructionsByStatus(params: {
      userId: string;
      transferStatus: Exclude<TransferStatusTypes, TransferStatusTypes.inProgress>[];
    }) {
      const { userId, transferStatus } = params;
      const transfers = await api.authCall<TransferInstructionResponse[]>({
        method: "GET",
        endpoint: `/api/userIdentity/${userId}/transfer/transferInstruction?transferStatus=${transferStatus.toString()}`,
      });
      const mappedTransfers = transfers.map((transfer) => TransferInstruction.fromAPI(transfer));
      return sortBy(mappedTransfers, (transfer) => transfer.source?.planSponsor.toLowerCase() || "");
    },

    /**
     * Returns a promise that when successful means they are confirmed; otherwise on error they aren't.
     */
    async confirmCheckMailedToProvider(params: { accessToken: string; transferInstructionId: string; userId: string }) {
      await api.authCall({
        body: {
          accessToken: params.accessToken,
        },
        method: "POST",
        endpoint: `/api/userIdentity/${params.userId}/transfer/transferInstruction/${params.transferInstructionId}/checkMailedToProvider`,
      });
    },

    async getProviderMailingDetails(params: { userId: string; transferInstructionId: string }) {
      return api.authCall<ProviderMailingDetails>({
        method: "GET",
        endpoint: `/api/userIdentity/${params.userId}/transfer/transferInstruction/${params.transferInstructionId}/transferProviderData`,
      });
    },

    async schedulePhoneCall(params: { transferInstructionId: number; userId: string; eventUrl: string }) {
      const { userId, transferInstructionId, eventUrl } = params;
      return api.authCall({
        method: "POST",
        endpoint: `/api/userIdentity/${userId}/transfer/transferInstruction/${transferInstructionId}/schedulePhoneCall`,
        body: {
          eventUrl,
        },
      });
    },

    async schedulePhoneCallZoho(params: { transferInstructionId: number; userId: string }) {
      const { userId, transferInstructionId } = params;
      return api.authCall({
        method: "POST",
        endpoint: `/api/userIdentity/${userId}/transfer/transferInstruction/${transferInstructionId}/schedulePhoneCall`,
      });
    },

    async getTransferInstructionsByTransferId(params: { userId: string; transferId: string }) {
      const { userId, transferId } = params;
      const response = await api.authCall<TransferInstructionResponse[]>({
        method: "GET",
        endpoint: `/api/userIdentity/${userId}/transfer/${transferId}/transferInstruction`,
      });
      return response.map((item) => TransferInstruction.fromAPI(item));
    },

    async getTransferInstruction(params: { userId: string; transferInstructionId: string }) {
      const { userId, transferInstructionId } = params;
      const response = await api.authCall<TransferInstructionResponse>({
        method: "GET",
        endpoint: `/api/userIdentity/${userId}/transfer/transferInstruction/${transferInstructionId}`,
      });
      return TransferInstruction.fromAPI(response);
    },

    async connectDestinationToTransferInstruction(params: {
      userId: string;
      transferInstructionId: string;
      destination: TransferDestination;
    }) {
      const { userId, transferInstructionId, destination } = params;
      return api.authCall({
        method: "POST",
        endpoint: `/api/userIdentity/${userId}/transfer/transferInstruction/${transferInstructionId}/addDestinationAccount`,
        body: {
          destinationType: destination.destinationType,
          destinationAccountId: destination.destinationAccountId,
        },
      });
    },

    async createOrUpdateDestinationBasedOnYetToOpen(params: {
      userId: string;
      transferInstructionId: string;
      destination: TransferDestination;
      destinationType?: DestinationType;
    }) {
      const { userId, transferInstructionId, destination, destinationType } = params;
      const transferInstruction = await methods.getTransferInstruction({ userId, transferInstructionId });
      let { destinationsYetToOpen } = transferInstruction;

      // If the destination account already exists in the non pending destinations, we should create a new one
      if (
        destination.destinationAccountId !== undefined &&
        transferInstruction.nonPendingIRADestinations.find(
          (dest) => dest.destinationAccountId === destination.destinationAccountId
        )
      ) {
        return methods.createDestination(params);
      }

      // Otherwise let's check against the destinations that have yet to open
      if (destinationType) {
        destinationsYetToOpen = transferInstruction.destinationsYetToOpen.filter((yetToOpenDestination) => {
          return yetToOpenDestination.destinationType === destinationType;
        });
      }
      if (destinationsYetToOpen.length) {
        const destinationNotOpenedYet = destinationsYetToOpen[0];
        return methods.updateDestination({
          ...params,
          destination: new TransferDestination({
            ...destination,
            destinationAccountId: destinationNotOpenedYet.destinationAccountId,
          }),
        });
      }
      return methods.createDestination(params);
    },

    async createDestination(params: {
      userId: string;
      transferInstructionId: string;
      destination: TransferDestination;
    }) {
      const { userId, transferInstructionId, destination } = params;
      const response = await api.authCall<TransferDestinationResponse[]>({
        method: "POST",
        endpoint: `/api/userIdentity/${userId}/transfer/transferInstruction/${transferInstructionId}/destinationAccount`,
        body: JSON.stringify(destination),
      });
      return TransferDestination.fromApi(response[0]);
    },

    async updateDestination(params: {
      userId: string;
      transferInstructionId: string;
      destination: TransferDestination;
    }) {
      const { userId, transferInstructionId, destination } = params;
      const response = await api.authCall<TransferDestinationResponse>({
        method: "POST",
        endpoint: `/api/userIdentity/${userId}/transfer/transferInstruction/${transferInstructionId}/destinationAccount/${destination.destinationAccountId}/update`,
        body: JSON.stringify(destination),
      });
      return TransferDestination.fromApi(response);
    },

    async updateOpenLoanUserDecision(params: {
      userId: string;
      transferInstructionId: number;
      openLoanUserDecision: OpenLoanUserDecision;
    }) {
      const { userId, transferInstructionId, openLoanUserDecision } = params;
      return api.authCall({
        method: "POST",
        body: { openLoanUserDecision },
        endpoint: `/api/userIdentity/${userId}/transfer/transferInstruction/${transferInstructionId}`,
      });
    },

    async updateIRAAccountOpeningStatus(params: { userId: string; id: string | number | undefined; status: string }) {
      const { userId, id, status } = params;
      if (!id) return;
      return api.authCall({
        method: "POST",
        overrideHeader: {
          "Content-Type": "text/plain",
        },
        body: status,
        endpoint: `/api/userIdentity/${userId}/transfer/destinationAccount/iraAccountOpeningData/${id}/updateStatus`,
      });
    },

    async createTestIRAAccountData(params: {
      userId: string;
      destinationAccountId: string | number;
      data: Record<string, string>;
    }) {
      const { userId, destinationAccountId, data } = params;
      return api.authCall({
        method: "POST",
        body: data,
        endpoint: `/api/userIdentity/${userId}/transfer/destinationAccount/${destinationAccountId}/iraAccountOpeningData`,
      });
    },

    // Requires ADMIN token
    async updateTransferInstructionStatus(params: {
      userId: string;
      transferInstructionId: string | number;
      transferStatus: TransferStatusTypes;
      transferUserInputReason?: string;
      transferCancelReason?: string;
      transferInitiationMethod?: TransferInitiationMethod;
    }) {
      const {
        userId,
        transferStatus,
        transferInitiationMethod,
        transferCancelReason,
        transferUserInputReason,
        transferInstructionId,
      } = params;
      return api.authCall({
        method: "POST",
        body: {
          transferStatus,
          transferCancelReason,
          transferUserInputReason,
          transferInitiationMethod,
        },
        endpoint: `/api/userIdentity/${userId}/transfer/transferInstruction/${transferInstructionId}`,
      });
    },

    async updateTransferInstructionStatusNew(params: {
      userId: string;
      transferInstructionId: string | number;
      transferInstructionStatus: NewTransferStatusType;
    }) {
      const { userId, transferInstructionStatus, transferInstructionId } = params;
      return api.authCall({
        method: "POST",
        body: {
          transferInstructionStatus,
        },
        endpoint: `/api/userIdentity/${userId}/transfer/transferInstruction/${transferInstructionId}`,
      });
    },
  };
  return methods;
}
